import axios from 'axios';
import appConfig from 'config';
import authApi from 'api/authApi';

import utils from 'api/utils';

class Api {
  constructor({ apiURL, clientId }) {
    this.apiURL = apiURL;
    this.clientId = clientId;
    this.isRefreshing = false;
    this.refreshSubscribers = [];
    this.credentials = {
      token_type: '',
      access_token: '',
      refresh_token: '',
    };

    this.callbacks = {
      onRenewTokenSuccess: () => {},
    };

    this.instance = axios.create({
      baseURL: this.apiURL,
    });

    this.instance.interceptors.response.use(
      this.doNothing,
      this.intercept401Error
    );
  }

  doNothing = (response) => response;

  setCallbacks = (callbacks) => {
    this.callbacks = callbacks;
  };

  onTokenRefreshed = (credentials) => {
    this.setCredentials(credentials);
    this.callbacks.onRenewTokenSuccess(credentials);
    this.refreshSubscribers = this.refreshSubscribers.filter((cb) => cb());
  };

  onTokenRefreshedError = () => {
    this.refreshSubscribers = this.refreshSubscribers.filter((cb) => cb(true));
  };

  subscribeTokenRefresh = (cb) => {
    this.refreshSubscribers.push(cb);
  };

  intercept401Error = (error) => {
    try {
      const {
        response: { status },
      } = error;

      if (status !== 401) {
        return Promise.reject(error);
      }

      const { config } = error;

      const originalRequest = config;

      if (!this.isRefreshing) {
        this.isRefreshing = true;
        authApi
          .refreshToken(this.credentials.refresh_token)
          .then((response) => {
            this.isRefreshing = false;
            const { data } = response;
            this.onTokenRefreshed(data);
          })
          .catch((err) => {
            this.onTokenRefreshedError(err);
          });
      }

      const retryOriginalRequest = new Promise((resolve, reject) => {
        this.subscribeTokenRefresh((err) => {
          if (!err) {
            originalRequest.headers.Authorization = this.getAuthHeader();
            resolve(axios(originalRequest));
          }
          reject(err);
        });
      });

      return retryOriginalRequest;
    } catch (err) {
      return Promise.reject(error);
    }
  };

  getAuthHeader = () =>
    `${this.credentials.token_type} ${this.credentials.access_token}`;

  setAuthHeader = () => {
    this.instance.defaults.headers.common.Authorization = this.getAuthHeader();
  };

  cleanAuthHeader = () => {
    this.instance.defaults.headers.common.Authorization = null;
  };

  setCredentials = (credentials) => {
    const {
      token_type: tokenType,
      access_token: accessToken,
      refresh_token: refreshToken,
    } = credentials;

    this.credentials = {
      token_type: tokenType,
      access_token: accessToken,
      refresh_token: refreshToken,
    };

    this.setAuthHeader();

    return this.instance;
  };

  fetchMe = () => this.authGet('users/me/');

  revokeToken = (token) =>
    this.post('auth/revoke-token/', {
      client_id: this.clientId,
      token,
    });

  getInstance = () => this.instance;

  get = (...params) => this.instance.get(...params);

  post = (...params) => this.instance.post(...params);

  put = (...params) => this.instance.put(...params);

  patch = (...params) => this.instance.patch(...params);

  delete = (...params) => this.instance.delete(...params);

  authGet = (...params) =>
    this.credentials && this.credentials.access_token
      ? this.instance.get(...params)
      : {};

  authPost = (...params) =>
    this.credentials && this.credentials.access_token
      ? this.instance.post(...params)
      : {};

  authPut = (...params) =>
    this.credentials && this.credentials.access_token
      ? this.instance.put(...params)
      : {};

  authPatch = (...params) =>
    this.credentials && this.credentials.access_token
      ? this.instance.patch(...params)
      : {};

  authDelete = (...params) =>
    this.credentials && this.credentials.access_token
      ? this.instance.delete(...params)
      : {};

  searchAddresses = (params) =>
    this.authGet(`addresses/?${utils.queryString(params)}`);

  createAddress = ({ address }) => this.authPost(`addresses/`, { ...address });

  updateAddress = ({ address }) =>
    this.authPatch(`addresses/${address && address.id}/`, { ...address });

  deleteAddress = ({ addressId }) => this.authDelete(`addresses/${addressId}/`);

  getCityName = ({ zipcode, country }) =>
    this.authGet(`city/${zipcode}/?country=${country}`);

  getOrders = ({ params }) =>
    this.authGet(`shipper-app/shipper_orders/?${utils.queryString(params)}`);

  getOrder = (id) => this.authGet(`shipper-app/shipper_orders/${id}/`);

  getOrderControlDocuments = (id) =>
    this.authGet(`shipper-app/shipper_orders/${id}/control_document/`);

  getRecurringOrders = ({ params }) =>
    this.authGet(`recurring_orders/?${utils.queryString(params)}`);

  getRecurringOrder = (id) => this.authGet(`recurring_orders/${id}/`);

  downloadCsv = ({ params }) =>
    this.authGet(`shipper_orders/csv/?${utils.queryString(params)}`);

  downloadPodsZip = ({ params }) =>
    this.authGet(
      `shipper_orders/cargo_manifests/?${utils.queryString(params)}`
    );

  getRoutes = ({ params }) =>
    this.authGet(`shipper-app/routes/?${utils.queryString(params)}`);

  doQuote = (quotes) =>
    this.post(`${this.apiURL.replace('v1', 'v2')}pricing_quote/`, quotes);

  updateUser = ({ userId, userData }) =>
    this.authPatch(`users/${userId}/`, { ...userData });

  getUsers = ({ shipperId }) =>
    this.authGet(
      `${this.apiURL.replace(
        'v1',
        'api'
      )}users/?shipper=${shipperId}&page_size=0`
    );

  sendUserInvitation = ({ shipperId, invitationUserData }) =>
    this.authPost(`shippers/${shipperId}/users/`, { ...invitationUserData });

  resendUserInvitation = ({ userId }) => {
    return this.authPut(
      `${this.apiURL.replace('v1', 'api')}users/${userId}/resend_invitation/`
    );
  };

  getRecurringOrderSuggestion = ({ shipperId, params }) =>
    this.authGet(
      `sales_intelligence/shipper/${shipperId}/ro_suggestions/?${utils.queryString(
        params
      )}`
    );

  dismissRecurringOrderSuggestion = ({ shipperId, suggestionId }) =>
    this.authPatch(
      `sales_intelligence/shipper/${shipperId}/ro_suggestions/${suggestionId}/dismiss/`
    );

  getStatus = () => {
    return this.authGet(
      `${this.apiURL.replace(
        'v1',
        'api'
      )}feature_flags/switches/show_page_down/`
    );
  };

  getHolidays = ({ country, zipcode, days = 31 }) => {
    const params = {
      zipcode,
      country_code: country,
      for_next_x_days: days,
    };

    return this.authGet(`upcoming_holidays/?${utils.queryString(params)}`);
  };

  initPaymentIntent = (id) => {
    return this.authPut(`shipper-app/shipper_orders/${id}/pay/`);
  };

  confirmPaymentIntent = ({ soId, intent_id }) => {
    return this.authPut(`shipper-app/shipper_orders/${soId}/confirm_intent/`, {
      intent_id,
    });
  };

  getTrackingData = ({ shipperOrderId, liveTrackingId }) => {
    return this.get(
      `shipper-app/live-tracking/${shipperOrderId}/?lt=${liveTrackingId}`
    );
  };

  getTimeWindows = ({ shipments, request_time }) => {
    return this.authPost(`shipper-time-windows/`, {
      shipments,
      request_time,
    });
  };

  getSuitableVehicleType = ({ params }) => {
    const url = `utils/suitable_vehicle/`;
    return this.authPost(url, {
      ...params,
    });
  };

  getCatalog = ({ params }) => {
    const url = `product/catalog/`;
    return this.authPost(url, {
      ...params,
    });
  };

  confirmQuote = ({ params }) => {
    const url = `shipper-app/shipper_orders/`;
    return this.authPost(url, {
      ...params,
    });
  };

  requestForAccess = (params) => {
    const url = `request_for_access/`;
    return this.post(url, params);
  };

  getWorkingLocations = () => {
    return this.get('shipper-app/working-areas/').then(({data}) => data)
  }
}

export default new Api({
  apiURL: appConfig.API_URL,
  clientId: appConfig.ONTRUCK_AUTH_CLIENTID,
});
